import React, { useState, useEffect } from 'react';
import { X, Megaphone, AlertTriangle, Info, CheckCircle } from 'lucide-react';

const AnnouncementSystem = () => {
    const [announcements, setAnnouncements] = useState([]);
    const [visibleItems, setVisibleItems] = useState([]);

    useEffect(() => {
        fetchAnnouncements();
    }, []);

    const fetchAnnouncements = async () => {
        try {
            const { getAuthHeaders } = await import('../utils/cookies');
            const headers = getAuthHeaders();
            const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/announcements/active`, {
                headers,
                credentials: 'include'
            });
            if (res.ok) {
                const data = await res.json();
                setAnnouncements(data);
                setVisibleItems(data);
            }
        } catch (error) {
            console.error("Failed to fetch announcements", error);
        }
    };

    const handleDismiss = async (id) => {
        // Hide locally immediately (Optimistic UI)
        setVisibleItems(prev => prev.filter(item => item.id !== id));

        try {
            // Persist dismissal to server
            const { getAuthHeaders } = await import('../utils/cookies');
            const headers = getAuthHeaders();
            await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/announcements/${id}/dismiss`, {
                method: 'POST',
                headers,
                credentials: 'include'
            });
        } catch (error) {
            console.error("Failed to dismiss announcement", error);
        }
    };

    if (visibleItems.length === 0) return null;

    // Separate by type
    const banners = visibleItems.filter(a => a.type === 'BANNER');
    const modals = visibleItems.filter(a => a.type === 'MODAL');

    return (
        <>
            {/* Context Banners - Floating Top Center */}
            <div className="fixed top-24 left-1/2 -translate-x-1/2 flex flex-col gap-3 w-[90%] max-w-[300px] z-[2000] pointer-events-none">
                {banners.map(banner => (
                    <div
                        key={banner.id}
                        className="bg-emerald-50 text-emerald-900 p-3.5 rounded-xl shadow-xl relative flex flex-col animate-in fade-in slide-in-from-top-4 duration-500 pointer-events-auto border border-emerald-200"
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-2 text-emerald-600 mb-1">
                                <Megaphone size={16} />
                                <span className="text-[11px] font-bold tracking-wider">Announcement</span>
                            </div>
                            <button
                                onClick={() => handleDismiss(banner.id)}
                                className="p-1 text-emerald-600 hover:bg-emerald-100 rounded-full transition-colors shrink-0 -mr-1 -mt-1"
                            >
                                <X size={16} />
                            </button>
                        </div>
                        <p className="text-sm font-medium leading-relaxed mt-1">{banner.message}</p>
                    </div>
                ))}
            </div>

            {/* Modals - Show only first one to avoid stacking chaos */}
            {modals.length > 0 && (
                <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/40 animate-in fade-in duration-300">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="bg-emerald-50 p-8 flex justify-center relative overflow-hidden border-b border-emerald-100">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-100 rounded-full -mr-16 -mt-16 blur-xl" />
                            <Megaphone size={56} className="relative z-10 drop-shadow-sm text-emerald-500" />
                        </div>
                        <div className="p-6 text-center bg-white">
                            <h3 className="text-lg font-bold text-slate-800 mb-2 tracking-tight">
                                {modals[0].title}
                            </h3>
                            <p className="text-sm text-slate-600 mb-6 font-medium">
                                {modals[0].message}
                            </p>
                            <button
                                onClick={() => handleDismiss(modals[0].id)}
                                className="w-full py-3 bg-emerald-500 text-white rounded-[14px] text-sm font-bold tracking-wide hover:bg-emerald-400 transition-all shadow-sm shadow-emerald-500/20 active:scale-[0.98]"
                            >
                                Got it
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AnnouncementSystem;
